<template>
  <div class="page-wrap">
    <template v-if="data">
      <!-- Top bar with single Download button -->
    <div class="sheet-toolbar">
      <img src="/assets/logo.png" alt="Vehitra World" class="logo" />
      <div class="badge-and-cta">
        <span
          class="status-badge"
          :class="isPaid ? 'badge-paid' : 'badge-pending'"
        >
          {{ headerText }}
        </span>
        <span class="number">#{{ data?.number }}</span>

        <!-- <button
          v-if="!exporting"
          class="btn-download"
          @click="exportPDF"
          data-html2canvas-ignore="true"
          aria-label="Download PDF"
        >
          Download PDF
        </button> -->
      </div>
    </div>

    <!-- Printable sheet -->
    <div ref="sheet" class="sheet relative overflow-hidden">
      <!-- Background Image for PDF -->
      <img :src="pdfBg" class="absolute inset-0 w-full h-full object-cover pointer-events-none opacity-25" data-html2canvas-ignore="false" aria-hidden="true" />
      
      <div class="relative z-10">
      <!-- Status Summary -->
      <section class="block">
        <h2 class="block-title">Booking Summary</h2>
        <div class="grid grid-3">
          <div class="cell">
            <div class="label">Booking Status:</div>
            <div class="value text-emerald-600 font-bold">Under Process</div>
            <div class="text-[11px] text-slate-500 mt-0.5">(one of our agent will get back to you shortly)</div>
          </div>
          <div class="cell">
            <div class="label">Payment Status:</div>
            <div class="value font-bold" :class="getPaymentStatusClass">
              {{ getPaymentStatusText }}
            </div>
          </div>
          <div class="cell">
            <div class="label">Booking Number:</div>
            <div class="value font-bold">#{{ data?.number }}</div>
          </div>
        </div>
      </section>

      <!-- Contact -->
      <section class="block">
        <h2 class="block-title">Contact</h2>
        <div class="grid grid-4">
          <div class="cell">
            <div class="label">Name:</div>
            <div class="value">{{ data.contact?.name || '—' }}</div>
          </div>
          <div class="cell">
            <div class="label">Email:</div>
            <div class="value">
              <template v-if="data.contact?.email">
                {{ data.contact.email }}
              </template>
              <template v-else>—</template>
            </div>
          </div>
          <div class="cell">
            <div class="label">Phone:</div>
            <div class="value">{{ data.contact?.phone || '—' }}</div>
          </div>
          <div class="cell">
            <div class="label">Comments:</div>
            <div class="value">{{ commentText }}</div>
          </div>
        </div>
      </section>

      <!-- Car -->
      <section v-if="data.car" class="block">
        <h2 class="block-title">Car</h2>
        <div class="grid grid-4">
          <div class="cell">
            <div class="label">Car:</div>
            <div class="value">{{ carDisplayName }}</div>
          </div>
          <div class="cell">
            <div class="label">Type:</div>
            <div class="value">{{ data.car?.type?.name || '—' }}</div>
          </div>
          <div class="cell">
            <div class="label">Bags:</div>
            <div class="value">{{ toDash(data.car_meta?.type?.bags ?? data.car?.type?.bags) }}</div>
          </div>
          <div class="cell">
            <div class="label">Passengers:</div>
            <div class="value">{{ toDash(data.car_meta?.type?.passengers ?? data.car?.type?.passengers) }}</div>
          </div>
          <div class="cell">
            <div class="label">Doors:</div>
            <div class="value">{{ toDash(data.car_meta?.type?.doors ?? data.car?.type?.doors) }}</div>
          </div>
        </div>
      </section>

      <!-- Locations -->
      <section class="block">
        <h2 class="block-title">Locations</h2>
        <div class="grid grid-3">
          <div class="cell">
            <div class="label">Pick Up:</div>
            <div class="value">
              {{ data.search?.pickUp?.location?.name || '—' }}
            </div>
          </div>

          <div class="cell">
            <div class="label">Date and time:</div>
            <div class="value">
              {{ data.search?.pickUp?.date || '—' }}
              <template v-if="data.search?.pickUp?.time">
                – {{ data.search.pickUp.time }}
              </template>
            </div>
          </div>

          <div
            v-if="data.search?.type === 'distance'"
            class="cell"
          >
            <div class="label">Drop Off:</div>
            <div class="value">
              {{ data.search?.dropOff?.location?.name || '—' }}
            </div>
          </div>

          <div
            v-if="data.search?.type === 'hourly'"
            class="cell"
          >
            <div class="label">Duration:</div>
            <div class="value">
              <template v-if="isFullDay">
                {{ days }} day<span v-if="days > 1">s</span>
                <span v-if="hoursPerDay" class="text-slate-500 font-normal"> (Daily {{ hoursPerDay }} hrs<span v-if="Number(kmPerDay) > 0"> &amp; {{ kmPerDay }} km</span>)</span>
                <span v-else class="text-slate-500 font-normal"> (Full Day)</span>
              </template>
              <template v-else>
                {{ hours }} hour<span v-if="Number(hours) > 1">s</span>
                <span v-if="hoursPerDay" class="text-slate-500 font-normal"> (Max {{ hoursPerDay }} hrs per day)</span>
              </template>
            </div>
          </div>
        </div>

        <!-- Pending note -->
        <!-- <div
          v-if="!isPaid"
          class="note note-warn"
        >
          <strong>Reservation Request received.</strong>
          Your booking is <em>awaiting confirmation</em>. We’ll notify you with a payment link and final voucher once confirmed.
        </div> -->
      </section>


      <!-- Policy -->
      <section class="block">
        <h2 class="block-title">Cancellation and amendment policy</h2>
        <div class="policy-box">
          <ul>
            <li>No-show charges will be 100%.</li>
            <li>
              Please review our
              <a href="/terms-and-conditions" target="_blank" rel="noopener">Terms &amp; Conditions</a>
              for cut-off times and fees.
            </li>
          </ul>
        </div>
      </section>
      </div>
    </div>

    <!-- (Optional) Remove any other download button on the page -->
    <!-- Delete your old bottom button OR guard it with v-if="false" -->
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, nextTick } from 'vue'
// @ts-ignore
import html2pdf from 'html2pdf.js'
import { getSessionPrefix } from '@/utils/common'   
import pdfBg from '@/assets/pdf_bg.jpg'   

const props = defineProps<{ data: any }>()
const data = props.data

const commentText = computed(() => {
  // try common server keys first
  const v =
    data?.contact?.comments ??
    data?.contact?.comment ??
    data?.comments ??
    data?.contact_comments ??
    data?.contact?.notes ??
    data?.notes ??
    data?.search?.comments ??
    data?.meta?.comments ??
    null

  if (typeof v === 'string' && v.trim() !== '') return v.trim()

  // fallback to what the user typed on the form (stored locally)
  try {
    const prefix = getSessionPrefix()
    const raw = localStorage.getItem(`${prefix}_contact_details`)
    if (raw) {
      const obj = JSON.parse(raw)
      if (typeof obj?.comments === 'string' && obj.comments.trim() !== '') {
        return obj.comments.trim()
      }
    }
  } catch {}

  return '—'
})

const carDisplayName = computed(() => {
  if (!data?.car) return '—'
  
  const maxPax = Number(data.car.type?.passengers || 0)
  const maxBags = Number(data.car.type?.bags || 0)
  const enteredPax = Number(data.car_meta?.type?.passengers ?? data.contact?.passengers ?? 0)
  const enteredBags = Number(data.car_meta?.type?.bags ?? data.contact?.bags ?? 0)
  const capacityExceeded = (enteredPax > 0 && enteredPax > maxPax) || (enteredBags > 0 && enteredBags > maxBags)
  
  if (data.status === 'need_confirmation' && capacityExceeded) {
    return `Vehicle Category (${data.car.type?.name || 'On Request'})`
  }
  
  return data.car.name || '—'
})

/* ===== Duration helpers ===== */
const hoursPerDay = computed(() => data?.car_meta?.hours_per_day ?? data?.car?.chaufferLocation?.hours_per_day ?? null)
const kmPerDay = computed(() => data?.car_meta?.km_per_day ?? data?.car?.chaufferLocation?.km_per_day ?? null)

const HOURS_PER_DAY = computed(() => Number(hoursPerDay.value || 10))
const hours = computed(() => Number(data?.search?.hours ?? 0))
const isFullDay = computed(() =>
  (data?.search?.package === 'full_day') ||
  (hours.value >= HOURS_PER_DAY.value && hours.value % HOURS_PER_DAY.value === 0)
)
const days = computed(() =>
  isFullDay.value ? Math.ceil(hours.value / HOURS_PER_DAY.value) : 0
)

/* ===== Status + money ===== */
const isPaid = computed(() => {
  const s = (data?.payment?.status || '').toString().toLowerCase()
  return ['paid', 'success', 'captured', 'confirmed'].includes(s) || data?.status === 'confirmed'
})
const isWalletPaid = computed(() => {
  return data?.payment?.wallet === 'wallet' || data?.payment_details?.wallet === 'wallet'
})
const getPaymentStatusText = computed(() => {
  if (!isPaid.value) return 'Not Paid'
  return isWalletPaid.value ? 'Agency Credit' : 'Paid'
})
const getPaymentStatusClass = computed(() => {
  if (!isPaid.value) return 'text-rose-600'
  return isWalletPaid.value ? 'text-blue-600' : 'text-emerald-600'
})
const headerText = computed(() => (isPaid.value ? 'Booking Voucher' : 'Reservation Request'))
const formattedTotal = computed(() => {
  const amount = Number(data?.totalAmount || 0)
  const currency = (data?.currency || 'USD').toUpperCase()
  return `${amount.toFixed(2)} ${currency}`
})
const toDash = (v: any) => (v === 0 || v ? String(v) : '—')
const capitalize = (s?: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '')

/* ===== Export to PDF ===== */
const sheet = ref<HTMLElement | null>(null)
const exporting = ref(false)

const exportPDF = async () => {
  if (!sheet.value || exporting.value) return
  exporting.value = true
  await nextTick()

  const name = isPaid.value ? `Voucher_${data?.number || 'booking'}` : `Reservation_Request_${data?.number || 'booking'}`

  const opt: any = {
    margin:       [12, 12, 18, 12],          // top, left/right, bottom
    filename:     `${name}.pdf`,
    image:        { type: 'jpeg', quality: 0.98 },
    html2canvas:  { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
    jsPDF:        { unit: 'pt', format: 'a4', orientation: 'portrait' },
    pagebreak:    { mode: ['css', 'avoid-all', 'legacy'] }
  }

  try {
    await html2pdf().from(sheet.value).set(opt).save()
  } finally {
    exporting.value = false
  }
}
</script>

<style scoped>
/* ===== Layout ===== */
.page-wrap { width: 100%; margin: 24px auto; }
.sheet-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 10px;
}
.logo { height: 36px; width: auto; }

.badge-and-cta { display: flex; align-items: center; gap: 10px; }
.number { color:#64748b; font-size: 13px; }

.btn-download {
  padding: 8px 12px; border-radius: 10px; border: 1px solid #d1d5db;
  background: #f8fafc; cursor: pointer; font-weight: 600; font-size: 13px;
}
.btn-download:hover { background:#eef2f7; }

/* ===== Badge ===== */
.status-badge {
  display:inline-flex; align-items:center; gap:8px;
  padding:6px 10px; border-radius: 999px; font-weight:600; font-size: 12px;
  border:1px solid transparent;
}
.badge-pending { background:#fff7ed; color:#92400e; border-color:#fed7aa; }
.badge-paid    { background:#ecfdf5; color:#065f46; border-color:#a7f3d0; }

/* ===== Printable “sheet” ===== */
.sheet {
  background:#fff;
  background-repeat: no-repeat;
  background-position: center;
  border:1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px;
}

/* Block titles + sections */
.block { margin-top: 14px; }
.block-title { font-size: 14px; font-weight: 700; color:#111827; margin: 0 0 8px; }

/* Grid */
.grid { display: grid; gap: 10px; }
.grid-4 { grid-template-columns: repeat(4, 1fr); }
.grid-3 { grid-template-columns: repeat(3, 1fr); }

@media (max-width: 900px) {
  .grid-4 { grid-template-columns: repeat(2, 1fr); }
  .grid-3 { grid-template-columns: repeat(1, 1fr); }
}

/* Cells with PDF-friendly borders (no box-shadow/ring) */
.cell {
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
  min-height: 52px;
}
.label { color:#6b7280; font-size: 12px; margin-bottom: 4px; }
.value { font-weight: 700; color:#0f172a; font-size: 13px; line-height: 1.35; }

/* Notes */
.note {
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  margin-top: 10px;
}
.note-warn { background:#fff7ed; border:1px solid #fed7aa; color:#92400e; }

/* Policy box */
.policy-box {
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
}
.policy-box ul { margin: 0; padding-left: 18px; }
.policy-box li { margin: 4px 0; font-size: 13px; }

/* Hide the top button inside the PDF/print */
@media print {
  .btn-download { display: none !important; }
  .page-wrap { margin: 0; }
}
</style>